import 'maplibre-gl/dist/maplibre-gl.css';
import type * as MapLibre from 'maplibre-gl';
import { workerUrl } from './mapEngine';
import { importEngine } from './engineImport';

/** The engine's namespace, as the module import resolves it. There is no
 *  default export: every use is `maplibregl.Map`, `maplibregl.Marker`, … */
export type MapLibreNamespace = typeof MapLibre;

let engine: Promise<MapLibreNamespace> | null = null;

/**
 * The map engine, loaded once (MapLibre plan D2).
 *
 * `importEngine()` loads the engine as a native ES module from its own
 * versioned public URL (`mapEngine.ts`), outside the webpack bundle, and only
 * when a map mounts — so a page without a map pays nothing for it. It rejects
 * when the module cannot load (a 404 on the URL); callers leave their pane
 * empty. The worker URL is set exactly once,
 * BEFORE the promise resolves, and every map component constructs its `Map`
 * inside this promise's `.then` — so no `Map` can exist before the engine
 * knows where its same-origin worker is. The order is structural, not a
 * convention each caller must remember.
 */
export const loadEngine = (): Promise<MapLibreNamespace> => {
  if (!engine) {
    engine = importEngine().then((maplibregl) => {
      maplibregl.setWorkerUrl(workerUrl());
      return maplibregl;
    }).catch((error: unknown) => {
      // A failed load is not memoised: the next map mounted retries it,
      // rather than every later map on the page staying blank until reload.
      engine = null;
      throw error;
    });
  }
  return engine;
};

/** Interaction limits, in the ENGINE's scale (one less than the server's —
 *  `viewport.ts` converts). Engine 17 is server 18, the server's ceiling;
 *  −1 is server 0. Fit caps are separate: they are camera commands, never
 *  limits (plan D7). */
export const ENGINE_MAX_ZOOM = 17;
export const ENGINE_MIN_ZOOM = -1;

/**
 * The CARTO basemap: the styles, the key they need, and the credit they are
 * drawn under. ONE definition, because a map crediting a source it no longer
 * draws is worse than one crediting nothing.
 *
 * A KEY IS REQUIRED. CARTO's basemap terms ask every customer — free tier
 * included — to send their own CARTO-issued Basemaps key, and unauthenticated
 * tiles come back stamped "API KEY REQUIRED" across the map. Every map on this
 * site has been serving that watermark.
 *
 * The key is NOT the Platform access token in the CARTO workspace. It is
 * requested at https://carto.com/basemaps/apikey/ and needs no CARTO account.
 * The parameter is `key`, read off CARTO's own FAQ
 * (https://docs.carto.com/faqs/carto-basemaps) rather than inferred: `api_key`
 * is ignored, so the key would be configured, billed and useless while the
 * watermark stayed.
 *
 * `REACT_APP_CARTO_BASEMAP_KEY` carries it, and CRA inlines it AT BUILD TIME.
 * TWO things must be true for that to reach production, and the PostHog key
 * taught both the hard way:
 *
 *   1. the DigitalOcean app spec must scope it **RUN_AND_BUILD_TIME**, or it
 *      is simply absent during `npm run build`;
 *   2. the root `Dockerfile` must declare `ARG REACT_APP_CARTO_BASEMAP_KEY`
 *      and re-export it as `ENV` before that build — App Platform passes
 *      build-scope variables to a Dockerfile build ONLY as declared build
 *      args, so scope alone leaves the bundle unkeyed.
 *
 * A test holds (2) against every `REACT_APP_*` this app reads.
 */
const CARTO_KEY = process.env.REACT_APP_CARTO_BASEMAP_KEY;

/** Whether the basemap is being requested with a key. Read by tests; without
 *  one the maps still draw, watermarked, which is a better failure than a
 *  blank rectangle. */
export const BASEMAP_KEYED = Boolean(CARTO_KEY);

/**
 * The vector styles (MapLibre plan D3): CARTO Positron GL, the vector edition
 * of the `light_all`/`light_nolabels` rasters the maps drew before. Each map
 * greys its own canvas with a CSS filter (Almanac decision 10).
 *
 * These URLs are NOT keyed: a GL style makes its own requests (TileJSON,
 * tiles, glyphs, sprite) that a `?key=` on the style URL never reaches. The
 * key travels on every request through `cartoTransformRequest`.
 */
export const STYLE_URLS = {
  /** Streets and labels — the plan and day maps. */
  base: 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json',
  /** No labels, for the world view — avoids mixed-language country names. */
  noLabels: 'https://basemaps.cartocdn.com/gl/positron-nolabels-gl-style/style.json',
};

/** CARTO's hosts and no other: the scheme, any number of subdomain labels,
 *  `cartocdn.com`, an optional port, then the end of the authority. A host
 *  that merely CONTAINS `cartocdn.com` (`cartocdn.com.evil.net`, or the
 *  string in a query) never receives the key. */
const CARTO_HOST = /^https:\/\/([A-Za-z0-9-]+\.)*cartocdn\.com(?::[0-9]+)?(?:[/?#]|$)/;

/**
 * The engine's `transformRequest` (plan D4): appends `key=<key>` to every
 * request bound for a CARTO host, and leaves every other request untouched.
 * With no key, nothing is changed and the maps draw watermarked.
 */
export const cartoTransformRequest = (key: string | undefined = CARTO_KEY) => (
  (url: string): MapLibre.RequestParameters => {
    if (!key || !CARTO_HOST.test(url)) return { url };
    const u = new URL(url);
    u.searchParams.set('key', key);
    return { url: u.toString() };
  }
);

/**
 * The credit CARTO's terms ask for, and OpenStreetMap's.
 *
 * Every map here credited `CARTO` alone, linked to carto.com's HOMEPAGE rather
 * than its attributions page, and named OpenStreetMap nowhere at all — while
 * the tiles are built from OSM data and OSM's guidelines ask for exactly this
 * linked form. Both names, both linked, one definition.
 */
const CREDITS = [
  { name: 'CARTO', href: 'https://carto.com/attributions', after: ', ' },
  { name: 'OpenStreetMap', href: 'https://www.openstreetmap.org/copyright', after: ' contributors' },
] as const;

/** The credit as markup, for the Leaflet Explore map until S3. */
export const BASEMAP_ATTRIBUTION = CREDITS
  .map((c) => `&copy; <a href="${c.href}">${c.name}</a>${c.after}`)
  .join('');

/**
 * The credit as an element (plan D5): each map turns the engine's own
 * attribution control off and draws this instead, built node by node from the
 * same two linked halves — never from an HTML string.
 */
export const creditElement = (): HTMLElement => {
  const credit = document.createElement('div');
  credit.className = 'map-credit';
  for (const c of CREDITS) {
    credit.appendChild(document.createTextNode('© '));
    const link = document.createElement('a');
    link.href = c.href;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = c.name;
    credit.appendChild(link);
    credit.appendChild(document.createTextNode(c.after));
  }
  return credit;
};

// Numbered pins: 18 px ink circles with a mono number (the design's `.pin`);
// the selected pin grows and wears a 2 px ink ring outside a paper gap
// (`.pin.sel`). Colours live in UnifiedMap.css as tokens.
//
// The painted disc is 18 px (30 selected), but the marker's box — what takes
// the click that opens the popup — is larger: 28 px, or 40 px selected so the
// ring sits inside it. The box is transparent and centres the disc
// (`.numbered-marker` in UnifiedMap.css); the engine anchors the box at its
// centre, which is the disc's centre.
export const NUMBERED_PIN = {
  disc: 18, box: 28, selectedDisc: 30, selectedBox: 40,
} as const;

/** A marker's element, and how far from its anchor its popup opens. */
export interface MarkerElement {
  element: HTMLElement;
  popupOffset: number;
}

const markerBox = (className: string, size: number): HTMLDivElement => {
  const el = document.createElement('div');
  el.className = className;
  el.style.width = `${size}px`;
  el.style.height = `${size}px`;
  return el;
};

export const numberedMarkerElement = (number: number, isHighlighted: boolean = false): MarkerElement => {
  const disc = isHighlighted ? NUMBERED_PIN.selectedDisc : NUMBERED_PIN.disc;
  const size = isHighlighted ? NUMBERED_PIN.selectedBox : NUMBERED_PIN.box;
  const element = markerBox(`numbered-marker${isHighlighted ? ' highlighted' : ''}`, size);
  const face = document.createElement('div');
  face.className = `marker-number${isHighlighted ? ' highlighted' : ''}`;
  const label = document.createElement('span');
  label.textContent = String(number);
  face.appendChild(label);
  element.appendChild(face);
  return { element, popupOffset: disc / 2 + 2 };
};

// Colours the engine draws (layer paint) cannot read a CSS variable, so a
// colour that must follow the tokens is read off the root's computed style (a
// bare var() is the fallback, which only DOM markers can use).
export const tokenColor = (name: string): string => {
  if (typeof document !== 'undefined') {
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    if (value) return value;
  }
  return `var(${name})`;
};

// Dots (week scope): the design's `.pin.dot` — an 11 px ink dot inside a
// 2 px paper edge. The fill follows the caller's colour, ink by default.
export const dotMarkerElement = (color: string = 'var(--text-primary)'): MarkerElement => {
  const element = markerBox('dot-marker', 11);
  const dot = document.createElement('div');
  dot.className = 'dot-marker__dot';
  dot.style.setProperty('background', color);
  element.appendChild(dot);
  return { element, popupOffset: 8 };
};

// Cluster markers (month scope): the count in a 36 px disc.
export const clusterMarkerElement = (count: number, color: string = 'var(--text-primary)'): MarkerElement => {
  const element = markerBox('cluster-marker', 36);
  const circle = document.createElement('div');
  circle.className = 'cluster-marker-circle';
  circle.style.setProperty('background', color);
  const label = document.createElement('span');
  label.textContent = String(count);
  circle.appendChild(label);
  element.appendChild(circle);
  return { element, popupOffset: 20 };
};
